import { useEffect, useContext } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { getOrganizationSettings } from '@core/api/getOrganizationSettings';
import useAuthReady from '@core/hooks/useAuthReady';
import { AuthContext } from '@core/providers/AuthProvider';
import { OrganizationSettings } from '@core/types/OrganizationSettings';

export type UseOrganizationSettingsResult = Omit<
  UseQueryResult<OrganizationSettings, any>,
  'data'
> & { organizationSettings: OrganizationSettings };

export const ORGANIZATION_SETTINGS_QUERY_KEY = 'OrganizationSettings';

const useOrganizationSettings = (orgId?: string): UseOrganizationSettingsResult => {
  const { organization } = useContext(AuthContext);
  const { isOrgReady, isAnonymous } = useAuthReady();

  useEffect(() => {
    const newOrganizationId = organization?.organizationId || orgId;

    if (newOrganizationId !== undefined) {
      sessionStorage.setItem('organizationId', newOrganizationId);
    }
  }, [organization?.organizationId, orgId]);

  const organizationId =
    organization?.organizationId || orgId || sessionStorage.getItem('organizationId') || undefined;

  // Authenticated callers wait for the product token. Anonymous shared-study
  // callers are allowed through when they pass an explicit `orgId`, because
  // StudyConfirmAuthWrapper's birthdate gate reads StudySecurity from this
  // response — gating it off would leave StudySecurity undefined and silently
  // disable that check. (The endpoint does serve anonymous callers.)
  //
  // Note this tests the `orgId` *prop*, not the resolved `organizationId`: the
  // resolved value falls back to sessionStorage, which is how the app-level
  // ThemeProvider used to fetch on anonymous routes using a stale org id left
  // over from an earlier session in the same tab.
  const canQuery = isOrgReady || (isAnonymous && !!orgId);

  const { data: organizationSettings = {}, ...rest } = useQuery({
    queryFn: () => getOrganizationSettings(organizationId),
    queryKey: [ORGANIZATION_SETTINGS_QUERY_KEY, organizationId],
    enabled: canQuery && organizationId !== undefined,
    staleTime: 1000 * 60 * 60 * 12,
    gcTime: 1000 * 60 * 60 * 12.5,
    refetchOnWindowFocus: false,
  });

  return { organizationSettings, ...rest };
};

export default useOrganizationSettings;
